import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query and re-render when its match state flips.
 *
 * SSR-safe: returns `false` on first render (when `window` is undefined)
 * and synchronizes inside `useEffect`. This avoids a hydration mismatch
 * for the typical case where the server can't know the viewport size.
 *
 * Pass the same string you'd put inside an `@media` block, e.g.
 * `useMediaQuery("(max-width: 639px)")`. Returns `true` while the query
 * matches the current viewport / pointing-device state.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia(query);
    // Sync once on mount in case the SSR default disagrees with the
    // actual current state.
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };
    // addEventListener is the modern API; Safari < 14 needs addListener.
    // We feature-detect rather than assume modern.
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}
